// Centralized mapping of API endpoints to Swedish error messages

import { EndpointErrorConfig } from "helpers/apiErrorMessages/apiErrorMessagesHelper";
import {
  emptyFoldersErrorHandler,
  threadRedirectOn404Handler,
  uploadErrorHandler
} from "./customErrorHandlers";

export type ErrorMessageMap = {
  [path: string]: EndpointErrorConfig;
};

export const API_ERROR_MESSAGES: ErrorMessageMap = {
  // Dashboard & Banners
  "/banner": {
    methods: {
      GET: "Hämtning av avisering från admin misslyckades.",
      POST: "Skapande av avisering misslyckades."
    },
    showNotification: true
  },
  "/banner/*": {
    methods: {
      PUT: "Uppdatering av avisering misslyckades.",
      DELETE: "Borttagning av avisering misslyckades."
    },
    showNotification: true
  },
  "/dashboard/*": {
    methods: {
      GET: "Hämtning av instrumentpanel misslyckades."
    },
    showNotification: true
  },

  // Messages & Threads
  "/messages/customer/*/latestUnreadThreads": {
    methods: {
      GET: "Hämtning av inkorgen misslyckades."
    },
    showNotification: true
  },
  "/messages/customer/*/threads": {
    methods: {
      GET: "Hämtning av konversationer misslyckades."
    },
    showNotification: true
  },
  "/messages/customer/*/threads/All": {
    methods: {
      GET: "Hämtning av konversationer misslyckades."
    },
    showNotification: true
  },
  "/messages/stats/summary": {
    methods: {
      GET: "Hämtning av meddelandestatistik misslyckades."
    },
    showNotification: true
  },
  "/messages/": {
    methods: {
      POST: "Skickning av meddelandet misslyckades."
    },
    showNotification: true
  },
  "/messages/customer/*/thread/*": {
    methods: {
      GET: "Hämtning av tråd misslyckades.",
      PUT: "Det gick inte att uppdatera trådstatus"
    },
    showNotification: true,
    customErrorHandler: threadRedirectOn404Handler
  },
  "/messages/customer/*/thread/*/category": {
    methods: {
      PUT: "Fel vid uppdatering av kategori"
    },
    showNotification: true
  },
  "/messages/archive/*/*": {
    methods: {
      PUT: "Fel vid arkivering av konversation"
    },
    showNotification: true
  },
  "/messages/unarchive/*/*": {
    methods: {
      PUT: "Fel vid återläsning av konversation från arkivet"
    },
    showNotification: true
  },
  "/messages/hide/*/*": {
    methods: {
      PUT: "Det gick inte att dölja meddelandet"
    },
    showNotification: true
  },
  "/messages/unhide/*/*": {
    methods: {
      PUT: "Det gick inte att återställa meddelandet"
    },
    showNotification: true
  },
  "/messages/sent/*": {
    methods: {
      GET: "Hämtning av meddelanden misslyckades."
    },
    showNotification: true
  },

  // Files & Documents
  "/files/*/latestUnhandled": {
    methods: {
      GET: "Hämtning av uppladdade filer misslyckades."
    },
    showNotification: true
  },
  "/files/v2/*": {
    methods: {
      POST: "Filöverföringen misslyckades."
    },
    showNotification: true,
    customErrorHandler: uploadErrorHandler
  },
  "/files/*/*": {
    methods: {
      GET: "Det gick inte att ladda ner filen."
    },
    showNotification: true
  },
  "/files/stats/summary": {
    methods: {
      GET: "Hämtning av filstatistik misslyckades."
    },
    showNotification: true
  },
  "/files/*/name/*": {
    methods: {
      PUT: "Ett fel uppstod vid uppdatering av filnamnet."
    },
    showNotification: true
  },
  "/files/*/move/*": {
    methods: {
      PUT: "Filöverföringen till dokumentarkivet misslyckades."
    },
    showNotification: true
  },
  "/files/handle/": {
    methods: {
      PUT: "Markering av fil som hanterad misslyckades."
    },
    showNotification: true
  },

  // Customers
  "/user/customers": {
    methods: {
      GET: "Vi kunde tyvärr inte hämta dina kunder just nu."
    },
    showNotification: true
  },
  "/user/customers/*": {
    methods: {
      GET: "Ett fel uppstod vid hämtning av kunden."
    },
    showNotification: true
  },
  "/user/customers/": {
    methods: {
      POST: "Sparande av inställningar misslyckades."
    },
    showNotification: true
  },
  "/customers/search": {
    methods: {
      GET: "Hämtning av kunddata misslyckades."
    },
    showNotification: true
  },
  "/customers/unassigned/search": {
    methods: {
      GET: "Sökning av otilldelade kunder misslyckades."
    },
    showNotification: true
  },
  "/user/customers/searchall": {
    methods: {
      GET: "Sökning av alla kunder misslyckades."
    },
    showNotification: true
  },

  // Documents (Skicka)
  "/skickaDocuments/*/counts": {
    methods: {
      GET: "Hämtning av dokumentantal misslyckades."
    },
    showNotification: true
  },
  "/skickaDocuments/*": {
    methods: {
      GET: "Hämtning av dokumentet misslyckades."
    },
    showNotification: true
  },

  // Archive
  "/archive/*/folders": {
    methods: {
      GET: "Dokumentarkivet kunde inte öppnas."
    },
    showNotification: true,
    customErrorHandler: emptyFoldersErrorHandler
  },
  "/archive/*": {
    methods: {
      POST: "Filöverföringen misslyckades."
    },
    showNotification: true,
    customErrorHandler: uploadErrorHandler
  },
  "/archive/*/folder/*": {
    methods: {
      GET: "Hämtning av arkivfiler misslyckades."
    },
    showNotification: true
  },
  "/archive/move/*": {
    methods: {
      PUT: "Flyttning av filen i dokumentarkivet misslyckades."
    },
    showNotification: true
  },
  "/archive/delete/*/*": {
    methods: {
      PUT: "Borttagning av filen misslyckades."
    },
    showNotification: true
  },
  "/archive/restore/*/*": {
    methods: {
      PUT: "Återställning av fil misslyckades."
    },
    showNotification: true
  },
  "/archive/*/*": {
    methods: {
      GET: "Det gick inte att ladda ner filen.",
      DELETE: "Permanent borttagning misslyckades."
    },
    showNotification: true
  },
  "/archive/external/*/*/delete": {
    methods: {
      DELETE: "Borttagning av extern användares fil misslyckades."
    },
    showNotification: true
  },

  // Inventory
  "/inventory/article_register": {
    methods: {
      GET: "Öppning av artikelregistret misslyckades."
    },
    showNotification: true
  },
  "/inventory/customers/*/inventories": {
    methods: {
      GET: "Hämtning av inventeringar misslyckades.",
      POST: "Skapande av inventering misslyckades."
    },
    showNotification: true
  },
  "/inventory/customers/*/inventories/*": {
    methods: {
      GET: "Hämtning av inventering misslyckades.",
      PUT: "Uppdatering av inventering misslyckades.",
      PATCH: "Uppdatering av inventeringens status misslyckades."
    },
    showNotification: true
  },
  "/inventory/customers/*/inventories/*/pdf": {
    methods: {
      POST: "Generering av PDF för inventeringen misslyckades."
    },
    showNotification: true
  },

  // User & Authentication
  "/user/": {
    methods: {
      GET: "Hämtning av användarinformation misslyckades.",
      POST: "Skapande av användare misslyckades."
    },
    showNotification: true
  },
  "/users/internal/": {
    methods: {
      GET: "Hämtning av interna användare misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/users/external": {
    methods: {
      GET: "Hämtning av externa användare misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/upsertExternalUsersForCustomer": {
    methods: {
      PUT: "Uppdatering av externa användare misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/addExternalUser/customer/*": {
    methods: {
      POST: "Lägg till extern användare misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/login": {
    methods: {
      POST: "Inloggning misslyckades."
    },
    showNotification: true
  },
  "/user/internalUserLogin": {
    methods: {
      POST: "Hämtning av intern användarinloggning misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/onetime-login": {
    methods: {
      POST: "Hämtning av användarinloggning misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/generate-onetime-login": {
    methods: {
      POST: "Generering av engångslänk misslyckades."
    },
    showNotification: true
  },
  "/user/activatepassword": {
    methods: {
      POST: "Aktivering av lösenord misslyckades."
    },
    showNotification: true
  },
  "/user/setnewpassword": {
    methods: {
      POST: "Inställning av nytt lösenord misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/setpassword": {
    methods: {
      POST: "Inställning av lösenord misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/activateuser": {
    methods: {
      POST: "Aktivering av användare misslyckades.",
      PUT: "Autentisering via nyckel misslyckades."
    },
    showNotification: true
  },
  "/user/externalUserLogin/resetpassword": {
    methods: {
      POST: "Aktiveringsmeddelandet kunde inte skickas."
    },
    showNotification: true
  },
  "/validate/*": {
    methods: {
      GET: "Validering av token misslyckades."
    },
    showNotification: true
  },
  "/admin/updateExternalUser": {
    methods: {
      PUT: "Ändring av extern användare misslyckades."
    },
    showNotification: true
  },

  // Notifications
  "/notifications": {
    methods: {
      GET: "Hämtning av avisering misslyckades."
    },
    showNotification: true
  },
  "/notification/*/read": {
    methods: {
      PUT: "Markering som läst misslyckades."
    },
    showNotification: true
  },
  "/notification/*/accepted": {
    methods: {
      PUT: "Systemet kunde inte bekräfta aviseringen."
    },
    showNotification: true
  },
  "/notification/*/rejected": {
    methods: {
      PUT: "Avböjande av aviseringen misslyckades."
    },
    showNotification: true
  },
  "/admin/sysNotification": {
    methods: {
      POST: "Utskicket av systemmeddelande misslyckades."
    },
    showNotification: true
  },
  "/admin/sysNotification/*": {
    methods: {
      DELETE: "Borttagning av aviseringen misslyckades."
    },
    showNotification: true
  },

  // Settings
  "/settings": {
    methods: {
      GET: "Systemet kunde inte läsa in kundinställningarna.",
      PUT: "Uppdatering av inställningar misslyckades."
    },
    showNotification: true
  },
  "/settings/outofoffice": {
    methods: {
      GET: "Hämtning av frånvaromeddelande misslyckades.",
      PUT: "Uppdatering av frånvaromeddelande misslyckades."
    },
    showNotification: true
  },

  // System Messages
  "/sysMessage": {
    methods: {
      GET: "Hämtning av systemmeddelande misslyckades."
    },
    showNotification: true
  },
  "/admin/sysMessage": {
    methods: {
      POST: "Systemet kunde inte leverera systemmeddelandet till den externa användaren.",
      PUT: "Uppdatering av systemmeddelande misslyckades."
    },
    showNotification: true
  },

  // Invoice Review
  "/invoice-review/offices": {
    methods: {
      GET: "Hämtning av kontor misslyckades."
    },
    showNotification: true
  },
  "/invoice-review/offices/listUA": {
    methods: {
      GET: "Hämtning av uppdragsansvariga misslyckades."
    },
    showNotification: true
  },
  "/invoice-review/offices/readyForUA": {
    methods: {
      GET: "Hämtning av uppdrag misslyckades."
    },
    showNotification: true
  },
  "/invoice-review/offices/resultStatistics": {
    methods: {
      GET: "Hämtning av resultatstatistik misslyckades."
    },
    showNotification: true
  },

  // Services
  "/customer/*/integration/*": {
    methods: {
      GET: "Hämtning av tjänstelänk misslyckades."
    },
    showNotification: true
  },

  // Support
  "/support": {
    methods: {
      GET: "Hämtning av supportinformation misslyckades."
    },
    showNotification: true
  },

  // Volume Analysis
  "/volume-analysis/*": {
    methods: {
      GET: "Hämtning av volymanalys misslyckades."
    },
    showNotification: true
  },

  // Fiscal Year
  "/fiscalyear/*": {
    methods: {
      GET: "Hämtning av räkenskapsår misslyckades."
    },
    showNotification: true
  },

  // Document Service
  "/documentService/*": {
    methods: {
      GET: "Hämtning av dokumenttjänst misslyckades.",
      PUT: "Uppdatering av dokumenttjänst misslyckades."
    },
    showNotification: true
  },

  // Admin & Statistics
  "/admin/customers/statistics": {
    methods: {
      GET: "Hämtning av kundstatistik misslyckades."
    },
    showNotification: true
  },
  "/admin/report/integrationmodules": {
    methods: {
      GET: "Hämtning av licensrapport misslyckades."
    },
    showNotification: true
  },
  "/customer/*/add": {
    methods: {
      POST: "Tillägg av kund misslyckades."
    },
    showNotification: true
  },
  "/customer/*/close": {
    methods: {
      POST: "Det gick inte att stänga kunden."
    },
    showNotification: true
  },

  // PnL Report
  "/pnlshort/*/*/*": {
    methods: {
      GET: "Hämtning av resultatrapport misslyckades."
    },
    showNotification: true
  },

  // Polling
  "/poll/*": {
    methods: {
      GET: "Hämtning av status misslyckades."
    },
    showNotification: true
  }
};
